import React, { useEffect } from 'react'
import * as yup from 'yup'
import { mergeDeepRight } from 'ramda'
import { useTheme } from 'styled'

import PageHero from 'components/PageHero'
import FormGrid from 'components/FormGrid'
import FormDescription from 'components/FormDescription'
import Input from 'components/Input'
import Label from 'components/Label'
import RadioGroup from 'components/RadioGroup'
import InputGroup from 'components/InputGroup'
import InputGrid from 'components/InputGrid'
import Button from 'components/Button'
import InputError from 'components/InputError'
import { withAlertProvider, AlertFeed, useAlert } from 'components/Alert'

import { requiredError, tooLongError } from 'messages/errors'
import { useFormFields, useYup, useApi } from 'hooks'
import useUserInfo from 'auth/useUserInfo'
import * as API from 'api'

import { Admin, UserGroup } from 'types'

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>
const initialFormFields: Omit<Admin, 'id'> = {
  gender: '',
  title: '',
  name: '',
  surname: '',
  email: '',
}

function ProfileAdminForm() {
  const [alert] = useAlert({ autoHide: false, dismissEnabled: false })
  const { spaces } = useTheme()
  const userInfo = useUserInfo()

  const [
    { data: profile, loading: loadingProfile, error: loadingProfileError },
    loadProfile,
  ] = useApi<[string], Admin>(API.getProfile)

  const { fields, values } = useFormFields(
    mergeDeepRight(initialFormFields, profile || {})
  )
  const { validate, errors } = useYup(values, validationSchema)

  const [
    {
      data: saveProfileResult,
      loading: savingProfile,
      error: saveProfileError,
    },
    saveProfile,
  ] = useApi(API.saveProfile)

  useEffect(() => {
    if (userInfo) {
      loadProfile(userInfo.attributes.sub)
    }
  }, [loadProfile, userInfo])

  useEffect(() => {
    if (loadingProfileError) {
      alert.error(
        'Beim Laden der Profildaten ist leider ein Fehler aufgetreten.'
      )
    }
  }, [alert, loadingProfileError])

  useEffect(() => {
    if (saveProfileError) {
      alert.error(
        'Beim Speichern der Profildaten ist leider ein Fehler aufgetreten.'
      )
    }
  }, [alert, saveProfileError])

  useEffect(() => {
    if (saveProfileResult) {
      alert.success('Profildaten wurden erfolgreich gespeichert.')
    }
  }, [alert, saveProfileResult])

  const handleProfileSave = async () => {
    const { isValid } = await validate()
    if (isValid) {
      if (!userInfo) {
        alert.error('Leider ist ein Fehler aufgetreten.')
        return
      }
      saveProfile({
        ...values,
        id: userInfo.attributes.sub,
        type: UserGroup.ADMIN,
      })
    }
  }

  const disabled = loadingProfile || savingProfile
  return (
    <>
      <PageHero>
        <PageHero.Title>Profil - Admin</PageHero.Title>
      </PageHero>
      <FormGrid>
        <FormDescription>
          <FormDescription.Heading as="h2">
            Personen Daten
          </FormDescription.Heading>
          <FormDescription.Content>
            Bitte geben Sie hier Ihre personenbezogenen Daten an.
          </FormDescription.Content>
        </FormDescription>
        <form onSubmit={evt => evt.preventDefault()}>
          <InputGrid>
            <AlertFeed limit={1} />
            <InputGrid.Item side={'left'} responsiveFullAt={'phone'}>
              <Label>Anrede</Label>
              <RadioGroup {...fields.gender}>
                <InputGroup>
                  <RadioGroup.Button
                    value="male"
                    style={{ flex: '1', textAlign: 'center' }}
                    disabled={disabled}
                  >
                    Herr
                  </RadioGroup.Button>
                  <RadioGroup.Button
                    value="female"
                    style={{ flex: '1', textAlign: 'center' }}
                    disabled={disabled}
                  >
                    Frau
                  </RadioGroup.Button>
                </InputGroup>
              </RadioGroup>
              {errors.gender && <InputError>{errors.gender}</InputError>}
            </InputGrid.Item>
            <InputGrid.Item side={'right'} responsiveFullAt={'phone'}>
              <Label>Titel</Label>
              <Input type="text" {...fields.title} disabled={disabled} />
              {errors.title && <InputError>{errors.title}</InputError>}
            </InputGrid.Item>
            <InputGrid.Item side={'left'} responsiveFullAt={'phone'}>
              <Label>Vorname</Label>
              <Input type="text" {...fields.name} disabled={disabled} />
              {errors.name && <InputError>{errors.name}</InputError>}
            </InputGrid.Item>
            <InputGrid.Item side={'right'} responsiveFullAt={'phone'}>
              <Label>Nachname</Label>
              <Input type="text" {...fields.surname} disabled={disabled} />
              {errors.surname && <InputError>{errors.surname}</InputError>}
            </InputGrid.Item>
            <div style={{ margin: spaces.stack.l }}>
              <Label>Email</Label>
              <Input
                type="text"
                value={userInfo ? userInfo.attributes.email : ''}
                disabled
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              style={{ justifySelf: 'start' }}
              onClick={handleProfileSave}
              disabled={disabled}
            >
              <span>Profil speichern</span>
            </Button>
          </InputGrid>
        </form>
      </FormGrid>
    </>
  )
}

const validationSchema = yup.object().shape({
  title: yup.string().max(40, tooLongError('Titel', 40)),
  gender: yup.string().required(requiredError('Anrede')),
  name: yup
    .string()
    .max(50, tooLongError('Vorname', 50))
    .required(requiredError('Vorname')),
  surname: yup
    .string()
    .max(50, tooLongError('Nachname', 50))
    .required(requiredError('Nachname')),
})

export default withAlertProvider(ProfileAdminForm)
